import { elementsDansLOrdre, formaterMontant, groupesPiece, grouperEnArbre } from '@admin-pmvb/core'
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { type DragEvent, useEffect, useMemo, useState } from 'react'
import { LogoConnu } from '../composants/Logo'
import { cleLogoDeNom } from '../composants/marques'
import { ChampIndication } from '../composants/Dictee'
import { ecrit, type Piece, requeteMoi, requetes, type VuePieces } from '../api'
import { BadgeAcheminement } from '../composants/Acheminement'
import { ACCEPT_FICHIERS, RecapDepot, SuiviDepot, useDepot } from '../composants/DepotPieces'
import { retenirFile } from '../composants/FileParcours'
import { Icone, type NomIcone } from '../composants/Icone'
import { BasculeRegroupement, ListeGroupee, useRegroupement } from '../composants/ListeGroupee'
import { ProvenancePiece } from '../composants/PucesAdresses'
import { EntetePage, Filtre, Onglets } from '../composants/Ui'
import {
  jourCourt,
  LIBELLES_STATUT_PIECE,
  LIBELLES_TRANSMISSION,
  LIBELLES_TYPE_PIECE,
} from '../libelles'

const ICONES_VUE: Record<VuePieces, NomIcone> = {
  recentes: 'schedule',
  non_rattachees: 'link_off',
  a_suivre: 'outbox',
  toutes: 'search',
}

const VUES: { vue: VuePieces; libelle: string; aide: string }[] = [
  {
    vue: 'recentes',
    libelle: 'Arrivées récentes',
    aide: 'Les trente derniers jours, sans les pièces rejetées : reçues par admin@, déposées ou venues d’une plateforme agréée.',
  },
  {
    vue: 'non_rattachees',
    libelle: 'Non rattachées',
    aide: 'En cours de lecture, ou à vérifier par un humain (entité introuvable, lecture peu sûre, montant contradictoire).',
  },
  {
    vue: 'a_suivre',
    libelle: 'À suivre',
    aide: 'Rattachées, pas encore rangées dans le Drive ou envoyées au cabinet : décision à prendre, réglage à compléter, échec, en cours.',
  },
  {
    vue: 'toutes',
    libelle: 'Recherche',
    aide: 'Par numéro, émetteur, objet, nom de fichier ou texte de la pièce ; sans accents ni majuscules, fautes de frappe tolérées.',
  },
]

const ICONES_TYPE: Record<Piece['type'], NomIcone> = {
  facture: 'receipt_long',
  avis: 'account_balance',
  courrier: 'mail',
  accuse_depot: 'verified',
  releve: 'account_balance',
  contrat: 'draw',
  fiche_paie: 'payments',
  autre: 'description',
}

export function BadgeStatutPiece({ p }: { p: Pick<Piece, 'statut' | 'transmission' | 'etat'> }) {
  const ton = p.statut === 'a_verifier' ? 'ton-alerte' : p.statut === 'rattachee' ? 'ton-ok' : ''
  // Rattachée : où en est l'acheminement (Drive, cabinet), plutôt que le seul envoi.
  const suivi = p.statut === 'rattachee' && p.etat !== 'termine'
  return (
    <>
      <span className={`badge ${ton}`}>{LIBELLES_STATUT_PIECE[p.statut]}</span>
      {suivi && <BadgeAcheminement etat={p.etat} />}
      {!suivi && p.transmission && LIBELLES_TRANSMISSION[p.transmission] && (
        <span className="badge">
          <Icone nom="forward_to_inbox" taille={13} />
          {LIBELLES_TRANSMISSION[p.transmission]}
        </span>
      )}
    </>
  )
}

export function LignePiece({ p, vue }: { p: Piece; vue?: VuePieces }) {
  const montant = p.montants?.aPayer ?? p.montants?.ttc ?? null
  return (
    <Link
      to="/pieces/$id"
      params={{ id: p.id }}
      search={{ vue }}
      className="ligne ligne-lien ligne-piece"
    >
      {cleLogoDeNom(p.emetteur) ? (
        <LogoConnu nom={p.emetteur} taille={40} />
      ) : (
        <span className="pastille-type">
          <Icone nom={ICONES_TYPE[p.type]} />
        </span>
      )}
      <div className="ligne-contenu">
        <div className="ligne-titre">
          <strong>{p.emetteur ?? p.nomFichier ?? 'Pièce'}</strong>
          <BadgeStatutPiece p={p} />
        </div>
        <div className="discret">
          {[
            LIBELLES_TYPE_PIECE[p.type],
            p.numero,
            p.entite ?? 'sans entité',
            p.dateDocument ? jourCourt(p.dateDocument) : null,
          ]
            .filter(Boolean)
            .join(' · ')}
          {' · '}
          <ProvenancePiece p={p} />
        </div>
        {p.extrait && <div className="discret extrait-piece">{p.extrait}</div>}
      </div>
      <span className="montant">{montant !== null ? formaterMontant(montant, p.devise) : ''}</span>
    </Link>
  )
}

/** Ouvre le choix de fichiers du bandeau de dépôt. */
function ouvrirDepot() {
  const fichiers = document.getElementById('fichiers-depot') as HTMLInputElement | null
  if (fichiers && !fichiers.disabled) fichiers.click()
}

function DeposerPiece() {
  const moi = useQuery(requeteMoi).data
  const entites = useQuery(requetes.entites)
  const ecrivables = (entites.data ?? []).filter((e) => !e.archivee && moi && ecrit(moi, e.id))
  const depot = useDepot()
  const [entiteId, setEntiteId] = useState('')
  const [indication, setIndication] = useState('')
  const [survol, setSurvol] = useState(false)

  function deposerFichiers(e: DragEvent) {
    e.preventDefault()
    setSurvol(false)
    depot.choisir([...e.dataTransfer.files])
  }

  async function envoyer() {
    await depot.envoyer({ ...(entiteId && { entiteId }), indication })
    setIndication('')
  }

  return (
    <section
      className={survol ? 'bandeau-depot survol' : 'bandeau-depot'}
      onDragOver={(e) => {
        e.preventDefault()
        setSurvol(true)
      }}
      onDragLeave={() => setSurvol(false)}
      onDrop={deposerFichiers}
    >
      <span className="tuile-icone">
        <Icone nom="upload_file" />
      </span>
      <div className="bandeau-depot-texte">
        <strong>Déposer des PDF ou XML</strong>
        <span className="discret">
          Glisser ici ou parcourir : rien ne part avant « Envoyer ». Le suivi reste ici et dans{' '}
          <Link to="/envois">Mes envois</Link>. Sur le téléphone : <Link to="/photo">Photo</Link>.
        </span>
      </div>
      <button type="button" className="bouton" disabled={depot.enCours} onClick={ouvrirDepot}>
        Parcourir
      </button>
      <input
        id="fichiers-depot"
        type="file"
        multiple
        hidden
        disabled={depot.enCours}
        accept={ACCEPT_FICHIERS}
        onChange={(e) => {
          depot.choisir([...(e.target.files ?? [])])
          e.target.value = ''
        }}
      />
      <div className="bandeau-depot-suite">
        <RecapDepot
          depot={depot}
          bloque={!moi?.administrateur && !entiteId ? 'Choisir l’entité.' : null}
          surEnvoyer={() => void envoyer()}
        >
          <Filtre
            icone="domain"
            libelle="Entité"
            id="entite-depot"
            valeur={entiteId}
            onChange={setEntiteId}
            options={[
              {
                valeur: '',
                libelle: moi?.administrateur ? 'À déterminer par la lecture' : 'Choisir l’entité…',
              },
              ...ecrivables.map((e) => ({ valeur: e.id, libelle: e.nom })),
            ]}
          />
          <ChampIndication id="indication-depot" valeur={indication} onChange={setIndication} />
        </RecapDepot>
        <SuiviDepot depot={depot} />
      </div>
    </section>
  )
}

export function Pieces({
  vue,
  recherche,
  onChangeVue,
  onRechercher,
}: {
  vue: VuePieces
  /** Recherche de « Toutes », lue dans l'adresse. */
  recherche: string
  onChangeVue: (vue: VuePieces) => void
  onRechercher: (texte: string) => void
}) {
  const [q, setQ] = useState(recherche)
  const liste = useQuery({
    ...requetes.pieces({ vue, ...(vue === 'toutes' && recherche && { q: recherche }) }),
    refetchInterval: vue === 'non_rattachees' || vue === 'a_suivre' ? 15_000 : false,
  })
  const recentes = useQuery(requetes.pieces({ vue: 'recentes' }))
  const nonRattachees = useQuery(requetes.pieces({ vue: 'non_rattachees' }))
  const aSuivre = useQuery(requetes.pieces({ vue: 'a_suivre' }))
  const comptes: Partial<Record<VuePieces, number | undefined>> = {
    recentes: recentes.data?.length,
    non_rattachees: nonRattachees.data?.length,
    a_suivre: aSuivre.data?.length,
  }
  const courante = VUES.find((v) => v.vue === vue)!
  const [grouper, setGrouper] = useRegroupement('pieces')
  // Émetteur puis type de pièce ; émetteur inconnu, à la fin.
  const arbre = useMemo(
    () =>
      grouper && liste.data
        ? grouperEnArbre(
            liste.data,
            [(p) => groupesPiece(p).emetteur, (p) => groupesPiece(p).sorte],
            'Émetteur inconnu',
          )
        : null,
    [grouper, liste.data],
  )
  // Ordre de parcours une à une depuis la fiche : celui de l'écran.
  useEffect(() => {
    if (liste.data) retenirFile(`pieces:${vue}`, arbre ? elementsDansLOrdre(arbre) : liste.data)
  }, [liste.data, vue, arbre])

  return (
    <main>
      <EntetePage
        surtitre="Documents"
        titre="Pièces"
        action={{ icone: 'upload_file', libelle: 'Déposer une pièce', onClick: ouvrirDepot }}
      />
      <div className="pile">
        <DeposerPiece />
        <div className="barre-outils">
          <Onglets
            valeur={vue}
            onChange={onChangeVue}
            defilants
            onglets={VUES.map((v) => ({
              valeur: v.vue,
              libelle: v.libelle,
              icone: ICONES_VUE[v.vue],
              compte: comptes[v.vue],
            }))}
          />
          <BasculeRegroupement actif={grouper} onChange={setGrouper} />
          <p className="discret">{courante.aide}</p>
        </div>
        {vue === 'toutes' && (
          <form
            className="recherche"
            onSubmit={(e) => {
              e.preventDefault()
              onRechercher(q.trim())
            }}
          >
            <label className="filtre">
              <Icone nom="search" />
              <input
                type="search"
                aria-label="Rechercher une pièce"
                placeholder="Numéro, émetteur, mot du texte…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </label>
            <button type="submit" className="bouton-discret">
              Rechercher
            </button>
          </form>
        )}
        <section className="carte carte-flush">
          {liste.isPending && <p className="discret vide">Chargement…</p>}
          {liste.data?.length === 0 && <p className="discret vide">Rien ici.</p>}
          {arbre ? (
            <ListeGroupee
              noeuds={arbre}
              cle={(p) => p.id}
              ligne={(p) => <LignePiece p={p} vue={vue} />}
            />
          ) : (
            liste.data?.map((p) => <LignePiece key={p.id} p={p} vue={vue} />)
          )}
        </section>
      </div>
    </main>
  )
}
